import { apiAiChatCompletions } from '../../../../api/ai';
import { getDefaultModelForTask } from '../../aiConfigRuntimeHelpers';
import { extractAssistantTextFromOpenRouter } from '../../aiResponseHelpers';
import { createDefaultRouterContract, tryParseRouterContract } from './schema';

const ROUTER_MAX_TOKENS = 360;
const RERANKER_MAX_TOKENS = 380;

const toSafeInteger = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.round(numeric));
};

const extractUsageMetrics = (response = null) => {
  const usage = response?.usage && typeof response.usage === 'object' ? response.usage : {};
  const promptTokens = toSafeInteger(
    usage.prompt_tokens
    ?? usage.input_tokens
    ?? usage.promptTokens
    ?? usage.inputTokens
  );
  const completionTokens = toSafeInteger(
    usage.completion_tokens
    ?? usage.output_tokens
    ?? usage.completionTokens
    ?? usage.outputTokens
  );
  const totalTokens = toSafeInteger(
    usage.total_tokens
    ?? usage.totalTokens
    ?? (promptTokens + completionTokens)
  );

  return {
    promptTokens,
    completionTokens,
    totalTokens
  };
};

const buildRouterPrompt = ({ userText = '', intentKey = 'ask', goalState = null, queryPlan = null } = {}) => {
  const queries = Array.isArray(queryPlan?.queries) ? queryPlan.queries.slice(0, 5) : [];
  const goalSummary = String(goalState?.summary || '').trim();
  const keywords = Array.isArray(goalState?.keywords) ? goalState.keywords.slice(0, 10) : [];

  return [
    'Return STRICT JSON only.',
    'Infer a universal retrieval routing contract from the user request.',
    `user_text: ${String(userText || '').trim() || 'n/a'}`,
    `heuristic_intent: ${String(intentKey || 'ask').trim() || 'ask'}`,
    `prior_goal_summary: ${goalSummary || 'n/a'}`,
    `prior_keywords: ${keywords.join(', ') || 'n/a'}`,
    `planned_queries: ${queries.join(' | ') || 'n/a'}`,
    'Fields required: intent, objective, output_mode, constraints, scope, semantic_lenses, required_evidence_types, query_rewrites, query_expansions, negative_filters, routing, confidence, needs_reranker, reason_codes.'
  ].join('\n');
};

export const executeRouterStage = async ({
  enabled = false,
  userText = '',
  intentKey = 'ask',
  goalState = null,
  queryPlan = null,
  timeoutMs = 2500,
  signal,
  emitStage,
  onModelAttempt,
  callModel = apiAiChatCompletions,
  model = null
} = {}) => {
  const defaultContract = createDefaultRouterContract();

  if (!enabled) {
    if (typeof emitStage === 'function') {
      emitStage('router', {
        status: 'skipped',
        decisionCode: 'router_disabled',
        reasonCodes: ['router_disabled'],
        confidenceOverall: 0,
        needsReranker: false
      });
    }
    return {
      status: 'skipped',
      decisionCode: 'router_disabled',
      reasonCodes: ['router_disabled'],
      routerContract: defaultContract,
      modelId: null
    };
  }

  const modelId = String(model || '').trim() || getDefaultModelForTask('chat.explore', 'intent');
  const effectiveTimeoutMs = Math.max(200, Number(timeoutMs || 2500));
  const timeoutController = (typeof AbortController === 'function') ? new AbortController() : null;
  const requestSignal = timeoutController ? timeoutController.signal : signal;
  let timeoutId = null;
  let parentAbortHandler = null;
  const attemptStart = Date.now();

  try {
    if (timeoutController) {
      if (signal?.aborted) {
        timeoutController.abort(signal.reason);
      } else if (signal && typeof signal.addEventListener === 'function') {
        parentAbortHandler = () => timeoutController.abort(signal.reason);
        signal.addEventListener('abort', parentAbortHandler, { once: true });
      }
      timeoutId = setTimeout(() => {
        timeoutController.abort(new Error('router_timeout'));
      }, effectiveTimeoutMs);
    }

    const response = await callModel({
      model: modelId,
      messages: [
        { role: 'system', content: 'You are a routing classifier for retrieval orchestration.' },
        {
          role: 'user',
          content: buildRouterPrompt({
            userText,
            intentKey,
            goalState,
            queryPlan
          })
        }
      ],
      temperature: 0,
      max_tokens: ROUTER_MAX_TOKENS,
      signal: requestSignal
    });

    const text = extractAssistantTextFromOpenRouter(response);
    const parsed = tryParseRouterContract(text);
    const routerContract = parsed || defaultContract;
    const decisionCode = parsed ? 'router_completed' : 'router_parse_fallback';
    const reasonCodes = parsed ? [] : ['router_parse_failed'];

    if (typeof emitStage === 'function') {
      emitStage('router', {
        status: 'ok',
        decisionCode,
        reasonCodes,
        confidenceOverall: Number(routerContract?.confidence?.overall || 0),
        needsReranker: Boolean(routerContract?.needs_reranker),
        model: modelId
      });
    }

    if (typeof onModelAttempt === 'function') {
      onModelAttempt({
        stageName: 'router',
        callKind: 'router',
        modelId,
        responseModel: String(response?.model || modelId || '').trim() || modelId,
        providerRequestId: String(response?.id || '').trim() || null,
        usage: extractUsageMetrics(response),
        status: 'success',
        durationMs: Date.now() - attemptStart,
        errorCode: null,
        errorMessage: null
      });
    }

    return {
      status: 'ok',
      decisionCode,
      reasonCodes,
      routerContract,
      modelId
    };
  } catch (error) {
    if (typeof onModelAttempt === 'function') {
      const errorMessage = String(error?.message || 'router_failed');
      const timeoutLike = /timeout|timed out|cancelled before completion/i.test(errorMessage);
      onModelAttempt({
        stageName: 'router',
        callKind: 'router',
        modelId,
        responseModel: null,
        providerRequestId: null,
        usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
        status: timeoutLike ? 'timeout' : 'failed',
        durationMs: Date.now() - attemptStart,
        errorCode: timeoutLike ? 'REQUEST_TIMEOUT' : (String(error?.code || '').trim() || null),
        errorMessage
      });
    }

    if (typeof emitStage === 'function') {
      emitStage('router', {
        status: 'failed',
        decisionCode: 'router_failed',
        reasonCodes: ['router_error'],
        confidenceOverall: 0,
        needsReranker: false,
        model: modelId,
        error: String(error?.message || 'router_failed')
      });
    }

    return {
      status: 'failed',
      decisionCode: 'router_failed',
      reasonCodes: ['router_error'],
      routerContract: defaultContract,
      modelId,
      error: String(error?.message || 'router_failed')
    };
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (signal && parentAbortHandler && typeof signal.removeEventListener === 'function') {
      signal.removeEventListener('abort', parentAbortHandler);
    }
  }
};

const buildRerankerPrompt = ({ userText = '', routerContract = null, items = [] } = {}) => {
  const compactItems = (Array.isArray(items) ? items : []).slice(0, 18).map((item, index) => ({
    id: index,
    source: String(item?.source || '').trim() || 'unknown',
    docId: Number(item?.meta?.docId || 0) || null,
    chunkHash: String(item?.meta?.chunkHash || '').trim() || null,
    score: Number(item?.score ?? item?.baseScore ?? 0),
    text: String(item?.text || '').slice(0, 320)
  }));

  return [
    'Return STRICT JSON only.',
    'Task: rerank retrieval candidates for relevance to the user request and routing contract.',
    `user_text: ${String(userText || '').trim() || 'n/a'}`,
    `router_contract: ${JSON.stringify(routerContract || {})}`,
    `candidates: ${JSON.stringify(compactItems)}`,
    'Return: {"order":[candidate_ids_in_best_first_order],"confidence":0.0,"reason_codes":["..."]}'
  ].join('\n');
};

const tryParseRerankerPayload = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    const firstBrace = raw.indexOf('{');
    const lastBrace = raw.lastIndexOf('}');
    if (firstBrace >= 0 && lastBrace > firstBrace) {
      try {
        parsed = JSON.parse(raw.slice(firstBrace, lastBrace + 1));
      } catch (nestedError) {
        parsed = null;
      }
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;

  const order = Array.from(new Set(
    (Array.isArray(parsed.order) ? parsed.order : [])
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value >= 0)
  ));

  return {
    order,
    confidence: Number.isFinite(Number(parsed.confidence))
      ? Math.max(0, Math.min(1, Number(parsed.confidence)))
      : 0,
    reasonCodes: Array.from(new Set(
      (Array.isArray(parsed.reason_codes) ? parsed.reason_codes : [])
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )).slice(0, 10)
  };
};

export const executeRerankerStage = async ({
  enabled = false,
  userText = '',
  routerContract = null,
  items = [],
  timeoutMs = 2500,
  signal,
  emitStage,
  onModelAttempt,
  callModel = apiAiChatCompletions,
  model = null
} = {}) => {
  const rows = Array.isArray(items) ? items : [];
  if (!enabled || rows.length < 2) {
    if (typeof emitStage === 'function') {
      emitStage('reranker', {
        status: 'skipped',
        decisionCode: enabled ? 'reranker_not_needed' : 'reranker_disabled',
        reasonCodes: [enabled ? 'too_few_candidates' : 'reranker_disabled'],
        confidenceOverall: 0,
        needsReranker: false
      });
    }
    return {
      status: 'skipped',
      decisionCode: enabled ? 'reranker_not_needed' : 'reranker_disabled',
      reasonCodes: [enabled ? 'too_few_candidates' : 'reranker_disabled'],
      items: rows,
      confidence: 0
    };
  }

  const modelId = String(model || '').trim() || getDefaultModelForTask('chat.explore', 'intent');
  const effectiveTimeoutMs = Math.max(200, Number(timeoutMs || 2500));
  const timeoutController = (typeof AbortController === 'function') ? new AbortController() : null;
  const requestSignal = timeoutController ? timeoutController.signal : signal;
  let timeoutId = null;
  let parentAbortHandler = null;
  const attemptStart = Date.now();

  try {
    if (timeoutController) {
      if (signal?.aborted) {
        timeoutController.abort(signal.reason);
      } else if (signal && typeof signal.addEventListener === 'function') {
        parentAbortHandler = () => timeoutController.abort(signal.reason);
        signal.addEventListener('abort', parentAbortHandler, { once: true });
      }
      timeoutId = setTimeout(() => {
        timeoutController.abort(new Error('reranker_timeout'));
      }, effectiveTimeoutMs);
    }

    const response = await callModel({
      model: modelId,
      messages: [
        { role: 'system', content: 'You are a strict reranker for retrieval candidates.' },
        { role: 'user', content: buildRerankerPrompt({ userText, routerContract, items: rows }) }
      ],
      temperature: 0,
      max_tokens: RERANKER_MAX_TOKENS,
      signal: requestSignal
    });

    const text = extractAssistantTextFromOpenRouter(response);
    const parsed = tryParseRerankerPayload(text);
    if (!parsed || !Array.isArray(parsed.order) || parsed.order.length === 0) {
      if (typeof onModelAttempt === 'function') {
        onModelAttempt({
          stageName: 'reranker',
          callKind: 'reranker',
          modelId,
          responseModel: String(response?.model || modelId || '').trim() || modelId,
          providerRequestId: String(response?.id || '').trim() || null,
          usage: extractUsageMetrics(response),
          status: 'success',
          durationMs: Date.now() - attemptStart,
          errorCode: null,
          errorMessage: null
        });
      }

      if (typeof emitStage === 'function') {
        emitStage('reranker', {
          status: 'ok',
          decisionCode: 'reranker_parse_fallback',
          reasonCodes: ['reranker_parse_failed'],
          confidenceOverall: 0,
          needsReranker: false,
          model: modelId
        });
      }
      return {
        status: 'ok',
        decisionCode: 'reranker_parse_fallback',
        reasonCodes: ['reranker_parse_failed'],
        items: rows,
        confidence: 0
      };
    }

    if (typeof onModelAttempt === 'function') {
      onModelAttempt({
        stageName: 'reranker',
        callKind: 'reranker',
        modelId,
        responseModel: String(response?.model || modelId || '').trim() || modelId,
        providerRequestId: String(response?.id || '').trim() || null,
        usage: extractUsageMetrics(response),
        status: 'success',
        durationMs: Date.now() - attemptStart,
        errorCode: null,
        errorMessage: null
      });
    }

    const byIndex = new Map(rows.map((item, index) => [index, item]));
    const seen = new Set();
    const reordered = [];
    parsed.order.forEach((idx) => {
      if (!byIndex.has(idx) || seen.has(idx)) return;
      reordered.push(byIndex.get(idx));
      seen.add(idx);
    });
    rows.forEach((item, index) => {
      if (!seen.has(index)) reordered.push(item);
    });

    if (typeof emitStage === 'function') {
      emitStage('reranker', {
        status: 'ok',
        decisionCode: 'reranker_completed',
        reasonCodes: Array.isArray(parsed.reasonCodes) ? parsed.reasonCodes : [],
        confidenceOverall: Number(parsed.confidence || 0),
        needsReranker: false,
        model: modelId
      });
    }

    return {
      status: 'ok',
      decisionCode: 'reranker_completed',
      reasonCodes: Array.isArray(parsed.reasonCodes) ? parsed.reasonCodes : [],
      items: reordered,
      confidence: Number(parsed.confidence || 0)
    };
  } catch (error) {
    if (typeof onModelAttempt === 'function') {
      const errorMessage = String(error?.message || 'reranker_failed');
      const timeoutLike = /timeout|timed out|cancelled before completion/i.test(errorMessage);
      onModelAttempt({
        stageName: 'reranker',
        callKind: 'reranker',
        modelId,
        responseModel: null,
        providerRequestId: null,
        usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
        status: timeoutLike ? 'timeout' : 'failed',
        durationMs: Date.now() - attemptStart,
        errorCode: timeoutLike ? 'REQUEST_TIMEOUT' : (String(error?.code || '').trim() || null),
        errorMessage
      });
    }

    if (typeof emitStage === 'function') {
      emitStage('reranker', {
        status: 'failed',
        decisionCode: 'reranker_failed',
        reasonCodes: ['reranker_error'],
        confidenceOverall: 0,
        needsReranker: false,
        model: modelId,
        error: String(error?.message || 'reranker_failed')
      });
    }
    return {
      status: 'failed',
      decisionCode: 'reranker_failed',
      reasonCodes: ['reranker_error'],
      items: rows,
      confidence: 0,
      error: String(error?.message || 'reranker_failed')
    };
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (signal && parentAbortHandler && typeof signal.removeEventListener === 'function') {
      signal.removeEventListener('abort', parentAbortHandler);
    }
  }
};
